import type { PurchaseTracking } from "./api/paymentApi";

const PIXEL_IDS = ["990564875433590", "3832327480340211", "914800572957436"];
const CONSENT_EVENTS = ["CookiebotOnConsentReady", "CookiebotOnAccept", "CookiebotOnDecline"];
type Pixel = ((...args: any[]) => void) & {
  callMethod?: (...args: any[]) => void;
  queue: any[][];
  push?: Pixel;
  loaded?: boolean;
  version?: string;
};
type MarketingWindow = Window & {
  fbq?: Pixel;
  _fbq?: Pixel;
  Cookiebot?: { consent?: { marketing?: boolean } };
};
const marketingWindow = window as MarketingWindow;
let initialized = false;
let listening = false;
let granted = false;
const sentPurchases = new Set<string>();

const hasConsent = () => !window.location.pathname.startsWith("/affiliate/") &&
  marketingWindow.Cookiebot?.consent?.marketing === true;

function syncConsent() {
  const consent = hasConsent();
  if (!consent) {
    if (initialized && granted) marketingWindow.fbq?.("consent", "revoke");
    granted = false;
    return;
  }

  if (!initialized) {
    if (!marketingWindow.fbq) {
      const pixel: Pixel = (...args) => {
        if (pixel.callMethod) pixel.callMethod(...args);
        else pixel.queue.push(args);
      };
      pixel.queue = [];
      pixel.push = pixel;
      pixel.loaded = true;
      pixel.version = "2.0";
      marketingWindow.fbq = pixel;
      marketingWindow._fbq = pixel;
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.appendChild(script);
    }
    marketingWindow.fbq("consent", "grant");
    PIXEL_IDS.forEach(id => marketingWindow.fbq!("init", id));
    initialized = true;
    granted = true;
    marketingWindow.fbq("track", "PageView");
  } else if (!granted) {
    marketingWindow.fbq?.("consent", "grant");
    granted = true;
  }
}

export function initializeMetaPixel() {
  if (!listening) {
    CONSENT_EVENTS.forEach(event => window.addEventListener(event, syncConsent));
    listening = true;
  }
  // Consent may have loaded before the application bundle.
  syncConsent();
}

export function trackCheckout() {
  try {
    initializeMetaPixel();
    if (hasConsent()) marketingWindow.fbq?.("track", "InitiateCheckout", {});
  } catch {
    // Marketing must never prevent the redirect to Stripe.
  }
}

// Keep waiting for consent while the success page is mounted. Persist only after
// consent, and use an in-memory fallback when browser storage is unavailable.
export function trackPurchase(purchase: PurchaseTracking): () => void {
  const stop = () => CONSENT_EVENTS.forEach(event => window.removeEventListener(event, attempt));
  const attempt = () => {
    if (!hasConsent()) return;
    const key = `wavemind:meta-purchase:${purchase.event_id}`;
    try {
      initializeMetaPixel();
      let alreadySent = sentPurchases.has(key);
      try { alreadySent = alreadySent || localStorage.getItem(key) === "1"; } catch { /* Storage blocked. */ }
      if (alreadySent) { stop(); return; }
      if (!marketingWindow.fbq || !Number.isFinite(purchase.value) || purchase.value < 0) return;
      marketingWindow.fbq("track", "Purchase", {
        currency: purchase.currency,
        value: purchase.value,
      }, { eventID: purchase.event_id });
      sentPurchases.add(key);
      try { localStorage.setItem(key, "1"); } catch { /* Use the in-memory fallback. */ }
      stop();
    } catch {
      // Tracking failures must not turn a successful payment into an error.
    }
  };
  CONSENT_EVENTS.forEach(event => window.addEventListener(event, attempt));
  attempt();
  return stop;
}
